/**
 * One way into the library, from every source a file can arrive by.
 *
 * The file picker, a drag from the Files app, a paste, and the OS share sheet all end here,
 * so a video asks the same question and a corrupt file produces the same sentence whichever
 * door it came through. Before this existed only the picker was wired: the drop, paste and
 * share-target implementations lived in a module nothing imported, and a shared file was
 * written into a cache and then deleted seven days later without ever being offered to
 * anyone.
 */

import { createLogger, tag } from '../core/log';
import { importAudioBlob, notify, reportFailure, type ImportResult } from './state';
import { confirmDialog } from '../ui/components/sheet';
import type { SampleAsset } from '../model/types';

const log = createLogger('import');

/** Extensions worth trying even when the OS hands over an empty or wrong MIME type. */
const AUDIO_EXTENSIONS = new Set([
  'wav', 'wave', 'aif', 'aiff', 'aifc', 'caf', 'mp3', 'm4a', 'aac', 'flac', 'ogg', 'oga', 'opus', 'webm', 'weba', 'amr', '3gp',
]);
const VIDEO_EXTENSIONS = new Set(['mp4', 'm4v', 'mov', 'avi', 'mkv', 'webm', '3gp', 'qt']);

function extensionOf(name: string): string {
  const match = /\.([^.]+)$/.exec(name.toLowerCase());
  return match?.[1] ?? '';
}

export function looksLikeVideo(file: File): boolean {
  if (file.type.startsWith('video/')) return true;
  if (file.type.startsWith('audio/')) return false;
  return VIDEO_EXTENSIONS.has(extensionOf(file.name));
}

/**
 * Whether this is worth handing to the decoder at all.
 *
 * Deliberately permissive: the MIME type a platform attaches to a dropped or pasted file is
 * frequently empty, and the extension is only ever a hint — the decoder does the real
 * identification from the magic bytes. This exists to keep a dropped PDF or a folder from
 * reaching the decoder, not to decide what audio is.
 */
export function looksImportable(file: File): boolean {
  if (file.size === 0) return false;
  if (file.type.startsWith('audio/') || file.type.startsWith('video/')) return true;
  const extension = extensionOf(file.name);
  return AUDIO_EXTENSIONS.has(extension) || VIDEO_EXTENSIONS.has(extension);
}

export type ImportEntryPoint = 'picker' | 'drop' | 'paste' | 'share';

function originFor(entry: ImportEntryPoint, video: boolean): SampleAsset['origin'] {
  if (video) return 'videoExtraction';
  return entry === 'share' ? 'shareSheet' : 'filesImport';
}

/** Asks once per batch whether the audio should be pulled out of the videos in it. */
function confirmVideoExtraction(count: number): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    confirmDialog({
      title: count === 1 ? 'Extract the audio from this video?' : `Extract the audio from ${count} videos?`,
      message:
        'PadLab keeps the sound and discards the picture. The original video is untouched. Nothing is imported until you say so, and only the audio is stored.',
      confirmTitle: 'Extract audio',
      cancelTitle: 'Not now',
      symbol: 'waveform.path',
      destructive: false,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
    }).present();
  });
}

export interface ImportOutcome {
  imported: ImportResult[];
  skipped: number;
  failed: number;
}

/**
 * Imports a batch. Videos are only ever decoded after an explicit yes, which is the
 * "option to extract the audio" the brief asks for — previously a video was handed straight
 * to `decodeAudioData` and tagged as extracted whether or not anyone had been asked.
 */

/**
 * Second attempt at a video, in real time, with the user told what that costs.
 *
 * Returns null when the browser cannot even play the file, which is the honest end of the
 * road: neither decoding nor playback works, and nothing this app does will change that.
 */
async function rescueVideoAudio(file: File, name: string): Promise<ImportResult | null> {
  const { engine } = await import('./state');
  const { extractAudioInRealtime, realtimeExtractionSupported } = await import('../audio/realtime-extract');
  const { processingOverlay } = await import('../ui/components/sheet');

  // The capture is a live playback, so the graph has to be running before it starts.
  await engine.unlock();
  const context = engine.ensureContext();
  if (!realtimeExtractionSupported(context)) return null;

  const controller = new AbortController();
  const overlay = processingOverlay({
    title: 'Taking the sound off that video',
    message: 'This one has to play through to be captured, so it runs in real time.',
    onCancel: () => controller.abort(),
  });
  overlay.present();

  try {
    const captured = await extractAudioInRealtime(file, context, {
      signal: controller.signal,
      onProgress: ({ elapsed, duration }) => {
        overlay.setProgress(duration === null || duration <= 0 ? null : Math.min(elapsed / duration, 1));
        const left = duration === null ? null : Math.max(0, Math.round(duration - elapsed));
        overlay.setMessage(left === null ? 'Capturing…' : `About ${left}s left.`);
      },
    });
    return await importAudioBlob(captured, name, 'videoExtraction');
  } finally {
    overlay.destroy();
  }
}

export async function importDroppedFiles(
  files: readonly File[],
  entry: ImportEntryPoint = 'picker',
): Promise<ImportOutcome> {
  const outcome: ImportOutcome = { imported: [], skipped: 0, failed: 0 };
  const usable = files.filter((file) => looksImportable(file));
  outcome.skipped += files.length - usable.length;
  if (usable.length === 0) {
    if (files.length > 0) {
      notify('Nothing to import', 'None of those files look like audio or video.');
    }
    return outcome;
  }

  const videos = usable.filter((file) => looksLikeVideo(file));
  let extractVideo = true;
  if (videos.length > 0) extractVideo = await confirmVideoExtraction(videos.length);

  for (const file of usable) {
    const video = looksLikeVideo(file);
    if (video && !extractVideo) {
      outcome.skipped += 1;
      continue;
    }
    const name = file.name.replace(/\.[^.]+$/, '') || 'Imported sound';
    try {
      const result = await importAudioBlob(file, name, originFor(entry, video));
      outcome.imported.push(result);
    } catch (error) {
      // A video the decoder refuses is not the end. `decodeAudioData` will not open a `.mov`,
      // which is what every iPhone records, so the single most likely video a person owns was
      // the one guaranteed to fail. Playing it and capturing the result works because Safari
      // will happily play what it will not decode.
      if (video) {
        try {
          const rescued = await rescueVideoAudio(file, name);
          if (rescued !== null) {
            outcome.imported.push(rescued);
            continue;
          }
        } catch (fallbackError) {
          if (fallbackError instanceof DOMException && fallbackError.name === 'AbortError') {
            outcome.skipped += 1;
            notify('Cancelled', 'Nothing was imported from that video.');
            continue;
          }
          outcome.failed += 1;
          log.warn('realtime extraction failed', { reason: tag(fallbackError instanceof Error ? fallbackError.name : 'unknown') });
          reportFailure(`No audio could be read from “${file.name}”`, fallbackError);
          continue;
        }
      }
      outcome.failed += 1;
      log.warn('import rejected', { entry: tag(entry), reason: tag(error instanceof Error ? error.name : 'unknown') });
      reportFailure(
        video
          ? `No audio could be read from “${file.name}”`
          : `“${file.name}” could not be imported`,
        error,
      );
    }
  }

  if (outcome.imported.length === 1) notify('Imported', outcome.imported[0]!.asset.name);
  else if (outcome.imported.length > 1) notify('Imported', `${outcome.imported.length} sounds are in your library.`);
  else if (outcome.skipped > 0 && outcome.failed === 0) notify('Nothing imported', 'No audio was taken from those files.');
  return outcome;
}

/** Files carried by a drag, filtered to entries that are actually files. */
export function filesFromDataTransfer(transfer: DataTransfer | null): File[] {
  if (transfer === null) return [];
  if (transfer.items.length > 0) {
    const files: File[] = [];
    for (const item of transfer.items) {
      if (item.kind !== 'file') continue;
      const file = item.getAsFile();
      if (file !== null) files.push(file);
    }
    if (files.length > 0) return files;
  }
  return [...transfer.files];
}

/** Files on the system clipboard, for a paste. */
export function filesFromClipboard(data: DataTransfer | null): File[] {
  return filesFromDataTransfer(data);
}

/**
 * Binds drag-and-drop file import to `root`.
 *
 * `dragover` must be cancelled or the browser navigates away to the dropped file, which on a
 * PWA means leaving the app. The highlight class is applied to `root` so the drop target is
 * visible rather than implied. Returns a disposer.
 */
export function bindFileDrop(
  root: HTMLElement,
  options: { onFiles?: (files: File[]) => void } = {},
): () => void {
  let depth = 0;
  const clear = (): void => {
    depth = 0;
    root.classList.remove('is-drop-target');
  };
  const onEnter = (event: DragEvent): void => {
    if (filesFromDataTransfer(event.dataTransfer).length === 0 && event.dataTransfer?.types.includes('Files') !== true) {
      return;
    }
    depth += 1;
    root.classList.add('is-drop-target');
  };
  const onOver = (event: DragEvent): void => {
    if (event.dataTransfer?.types.includes('Files') !== true) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };
  const onLeave = (): void => {
    depth -= 1;
    if (depth <= 0) clear();
  };
  const onDrop = (event: DragEvent): void => {
    const files = filesFromDataTransfer(event.dataTransfer);
    if (files.length === 0) return;
    event.preventDefault();
    clear();
    if (options.onFiles !== undefined) options.onFiles(files);
    else void importDroppedFiles(files, 'drop');
  };

  root.addEventListener('dragenter', onEnter);
  root.addEventListener('dragover', onOver);
  root.addEventListener('dragleave', onLeave);
  root.addEventListener('drop', onDrop);
  return (): void => {
    root.removeEventListener('dragenter', onEnter);
    root.removeEventListener('dragover', onOver);
    root.removeEventListener('dragleave', onLeave);
    root.removeEventListener('drop', onDrop);
    clear();
  };
}

/**
 * Binds clipboard file import while `root` is on screen.
 *
 * The listener is on `document` because a paste is only delivered to the focused element,
 * and on a soundboard the focus is usually a pad. Pastes that carry no file are left alone,
 * so text fields keep working normally.
 */
export function bindClipboardImport(options: { onFiles?: (files: File[]) => void } = {}): () => void {
  const onPaste = (event: ClipboardEvent): void => {
    const target = event.target as HTMLElement | null;
    if (target !== null && (target.isContentEditable || target.closest('input, textarea') !== null)) return;
    const files = filesFromClipboard(event.clipboardData).filter((file) => looksImportable(file));
    if (files.length === 0) return;
    event.preventDefault();
    if (options.onFiles !== undefined) options.onFiles(files);
    else void importDroppedFiles(files, 'paste');
  };
  document.addEventListener('paste', onPaste);
  return (): void => document.removeEventListener('paste', onPaste);
}

// MARK: - Project backups

/**
 * Opens a file picker for a `.padlab` package, imports it, and opens the board.
 *
 * Lives here rather than on the Home screen because Home is not the only place that needs it.
 * The banner shown when someone follows a project link they do not have tells them to import
 * a backup, and until this existed there was no way for that banner to actually do it: the
 * advice named a recovery the interface did not offer.
 */
export async function importProjectBackup(): Promise<void> {
  const file = await pickFile('.padlab,application/octet-stream');
  if (file === null) return;
  try {
    const { projectStore, openProject } = await import('./state');
    const imported = await projectStore().importPackage(file);
    notify('Project imported', imported.name);
    if (await openProject(imported.id)) {
      const { navigate } = await import('./router');
      await navigate({ name: 'board', projectID: imported.id });
    }
  } catch (error) {
    reportFailure('That backup could not be opened', error);
  }
}

/**
 * A one-shot file picker. Resolves with the chosen file, or null if it was dismissed.
 *
 * The input is created and destroyed per call: a permanent hidden input is a control sitting
 * in the accessibility tree that nothing points at, and it keeps the chosen `File` alive.
 */
export function pickFile(accept: string): Promise<File | null> {
  return new Promise<File | null>((resolve) => {
    if (typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.hidden = true;
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');

    let settled = false;
    const finish = (file: File | null): void => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(file);
    };

    input.addEventListener('change', () => finish(input.files?.[0] ?? null));
    // Without `cancel`, dismissing the dialog would leave this promise pending forever.
    input.addEventListener('cancel', () => finish(null));
    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Exports the open board as a `.padlab` package and hands it to the system share sheet.
 *
 * The other half of `importProjectBackup`, and kept beside it deliberately: sending a board
 * and receiving one are one feature, and splitting them across screens is how the app ended
 * up able to import boards with no obvious way to send one.
 */
export async function shareProjectBackup(projectID: string, name: string): Promise<void> {
  try {
    const { projectStore } = await import('./state');
    const { shareContent } = await import('../platform/share');
    const blob = await projectStore().exportPackage(projectID);
    const filename = projectStore().packageFileName(name);
    const file = new File([blob], filename, { type: 'application/octet-stream' });
    const result = await shareContent({ title: name, files: [file] });
    if (result.outcome === 'downloaded') notify('Saved to your downloads', filename);
    else if (result.outcome === 'shared') notify('Shared');
  } catch (error) {
    reportFailure('That board could not be shared', error);
  }
}
